import type { Table } from '@tanstack/react-table';
import { PaginationBar, showsPaginationBar } from '@/components/ui/pagination-bar';

interface DataTablePaginationProps<TData> {
  table: Table<TData>;
  /** Total rows across every page; server-paginated tables must pass it. */
  totalRows?: number;
  pageSizes?: readonly number[];
}

const view = <TData,>(table: Table<TData>, totalRows?: number) => {
  const { pageIndex, pageSize } = table.getState().pagination;
  return {
    page: pageIndex + 1,
    pageSize,
    pageCount: table.getPageCount(),
    rowCount: totalRows ?? table.getFilteredRowModel().rows.length,
  };
};

/** See showsPaginationBar — this is the TanStack-shaped way to ask the same thing. */
export function showsPagination<TData>(table: Table<TData>, totalRows?: number): boolean {
  return showsPaginationBar(view(table, totalRows));
}

/** Adapts a TanStack table onto the shared PaginationBar. */
export function DataTablePagination<TData>({
  table,
  totalRows,
  pageSizes,
}: DataTablePaginationProps<TData>) {
  return (
    <PaginationBar
      {...view(table, totalRows)}
      onPageChange={(page) => table.setPageIndex(page - 1)}
      onPageSizeChange={(size) => table.setPageSize(size)}
      pageSizes={pageSizes}
    />
  );
}
